/* eslint-disable no-bitwise */
/* eslint-disable quote-props */
import { Injectable } from "@angular/core";
import {
	ProjectStatus, TimeUnit, WeekdayFC,
	ContactType, DocumentType, TerminalMediaType, AbsenceCreditAccountType,
	AbsenceVerificationType, AbsencePaymentType, CheckTimeRounding,
	TerminalCheckinMode, ScheduledTaskStatus, TerminalTrackingEvents,
	WorkHoursType, OpeningTimeType, VisibilityType,
	VisibilityTargetLeader, VisibilityName, DayOfWeek, ShiftFailureReason,
	CheckinStatus, SurchargeValueType, SpecialDayRecurrence, AbsenceValue,
	AccountingAccountType, HmmCredentialType, RFIDType, AccountStatementsFormat,
	Datev_FlagWorkIncapacity, BookingMode, CustomerMode, NameFormat, TagType, EventOrigin, YearAvailability,
	BookingAvailability,
	EventInstanceState, BookingType,
	EUnsettledBooking,
	EbicsContactStatus, TseConfigurationMode,
} from '@vierkant-software/types__api';
import { DateTime } from "luxon";
import { FileType, Mask } from "src/app/components/v-file-upload/file-upload.component";
import { AppService } from "src/services/app.service";
import { formatDateTime } from "src/util/formatting/date";
import { formatTime } from "src/util/formatting/time";
import { formatUser } from "src/util/formatting/user";
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { Option } from "src/util/types/global";

// #region helpers
function readonly(proto: unknown, key: string | number | symbol) {
    Object.freeze((<Record<string | number | symbol, unknown>>proto)[key]);
    Object.seal((<Record<string | number | symbol, unknown>>proto)[key]);
    if (Array.isArray((<Record<string | number | symbol, unknown>>proto)[key])) {
        (<Record<string | number | symbol, unknown[]>>proto)[key].forEach(
            (k: string | number | symbol) => readonly((<Record<string | number | symbol, unknown>>proto)[key], k)
        );
    }
    else if (typeof (<Record<string | number | symbol, unknown>>proto)[key] === 'object') {
        Object.keys((<Record<string | number | symbol, unknown>>proto)[key]).forEach(
            (k) => readonly((<Record<string | number | symbol, unknown>>proto)[key], k)
        );
    }
}

type StaticGlobals = typeof Globals;
declare module './globals.service' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface Globals extends StaticGlobals { }
}

// #endregion

/**
 * These tokens are not recognized by the language generator.
 * Add tokens manually to prevent them from being deleted on new generations.
 */
_([
	"CREDENTIAL.INFO.NO_CONTRACTS",
	"CREDENTIAL.INFO.NO_ACTIVE_CONTRACT",
	"SHIFT.CONTEXT_MENU.DUPLICATE",
	"SHIFT.CONTEXT_MENU.DELETE",
	"DATE.NAME.0",
	"DATE.NAME.1",
	"DATE.NAME.2",
	"DATE.NAME.3",
	"DATE.NAME.4",
	"DATE.NAME.5",
	"DATE.NAME.6",
	"DATE.NAME.7",
	"DATE.TOKENS.YEAR",
	"DATE.TOKENS.MONTH",
	"DATE.TOKENS.DAY",
	"DATE.TOKENS.HOUR",
	"DATE.TOKENS.MINUTE",
	"DATE.TOKENS.SECOND",
]);


@Injectable()
export class Globals {
    constructor() { throw new Error('Globals is not meant to be instantiated'); }

    @readonly static fUser = formatUser;
    @readonly static fTime = formatTime;
    @readonly static fDate = formatDateTime;

    /**
     * Lightness for profile color in light mode
     */
    @readonly static LIGHT_MODE_LIGHTNESS = 0.35;

    /**
     * Lightness for profile color in dark mode
     */
    @readonly static DARK_MODE_LIGHTNESS = 0.5;

    @readonly
    static timeUnit = {
        default: {
            [TimeUnit.None]:       "",
            [TimeUnit.Milisecond]: "Millisekunden",
            [TimeUnit.Second]:     "Sekunden",
            [TimeUnit.Minute]:     "Minuten",
            [TimeUnit.Hour]:       "Stunden",
            [TimeUnit.Day]:        "Tage",
            [TimeUnit.Week]:       "Wochen",
            [TimeUnit.Month]:      "Monate",
            [TimeUnit.Year]:       "Jahre",
        },
        short: {
            [TimeUnit.None]:       "",
            [TimeUnit.Milisecond]: "Ms",
            [TimeUnit.Second]:     "Sek",
            [TimeUnit.Minute]:     "Min",
            [TimeUnit.Hour]:       "h",
            [TimeUnit.Day]:        "T",
            [TimeUnit.Week]:       "W",
            [TimeUnit.Month]:      "M",
            [TimeUnit.Year]:       "J",
        },
        adjective: {
            [TimeUnit.None]:       "",
            [TimeUnit.Milisecond]: "ms",
            [TimeUnit.Second]:     "sek",
            [TimeUnit.Minute]:     "min",
            [TimeUnit.Hour]:       "h",
            [TimeUnit.Day]:        "t",
            [TimeUnit.Week]:       "w",
            [TimeUnit.Month]:      "mtl",
            [TimeUnit.Year]:       "j",
        }
    };

    @readonly
    static DateTime = {
        TIME_SIMPLE:           DateTime.TIME_SIMPLE,
        TIME_24_SIMPLE:        DateTime.TIME_24_SIMPLE,
        DATE_SHORT:            DateTime.DATE_SHORT,
        DATE_HUGE:             DateTime.DATE_HUGE,
        DATETIME_SHORT:        DateTime.DATETIME_SHORT,
        DATETIME_FULL:         DateTime.DATETIME_FULL,
        DATETIME_HUGE:         DateTime.DATETIME_HUGE,
        DATE_MED_WITH_WEEKDAY: DateTime.DATE_MED_WITH_WEEKDAY,
        YEAR_ONLY:             {
            year: "numeric"
        },

        /**
         * Show the current day, month, without the year.
         * Formats to `DD. M.` in `DE-DE`.
         */
        DATE_NO_YEAR: {
            day:   "numeric",
            month: "numeric",
        },
        /**
         * Show the current day, month and weekday, without the year.
         * Formats to `cccc, DD. M.` in `DE-DE`.
         *
         * @example Monday, 14.04
         */
        WEEKDAY_DATE_NO_YEAR: {
            day:     "numeric",
            month:   "numeric",
            weekday: "long",
        },
        /**
         * Show the current day, month and weekday, without the year.
         * Formats to `ccc, DD. M.` in `DE-DE`.
         *
         * @example Mon, 14.04
         */
        WEEKDAY_SHORT_DATE_NO_YEAR: {
            day:     "numeric",
            month:   "numeric",
            weekday: "short",
        },
    };

    @readonly
    static ContactType = {
        Undefined: ContactType.Undefined,
        EMail:     ContactType.EMail,
        Phone:     ContactType.Phone,
        Mobile:    ContactType.Mobile,
        Fax:       ContactType.Fax	,
    };

    static toWeekDayFC(day: WeekdayFC) {
        // eslint-disable-next-line no-restricted-syntax
        const dayNames = AppService.instance.getTranslation("dayNames") as string[];
        return day ? (dayNames[day] ?? 'INVALID') : 'INVALID';
    }

    @readonly
    static DayOfWeek = DayOfWeek;

    @readonly
    static projectStatusLabels = {
        [ProjectStatus.none]:                   '',
        [ProjectStatus.planning]:               'In Planung',
        [ProjectStatus.active]:                 'Aktiv',
        [ProjectStatus.pause]:                  'Pausiert',
        [ProjectStatus.inactive]:               'Inaktiv',
        [ProjectStatus.completed]:              'Abgeschlossen',
        [ProjectStatus.completedAutomatically]: 'Automatisch beendet',
    };

    @readonly
    static checkinStatus = {
        none:      CheckinStatus.none,
        checkedin: CheckinStatus.checkedin,
        inPause:   CheckinStatus.inPause
    };

    @readonly
    static yearAvailability = {
        None:      YearAvailability.None,
        Enabled:   YearAvailability.Enabled,
        Available: YearAvailability.Available
    };

    @readonly
    static specialDayRecurrence = {
        None:     SpecialDayRecurrence.None,
        Yearly:   SpecialDayRecurrence.Yearly,
        Variable: SpecialDayRecurrence.Variable
    };

    @readonly
    static absenceValue = {
        None: AbsenceValue.None,
        Half: AbsenceValue.Half
    };

    @readonly
    static openingTimeType = {
        Regular:    OpeningTimeType.Regular,
        Closed:     OpeningTimeType.Closed,
        Changed:    OpeningTimeType.Changed,
        LikeSunday: OpeningTimeType.LikeSunday
    };

    @readonly
    static publishTypeLabels = {
        never:           'Entwurf',
        automaticallyAt: 'Anstehende Änderung',
        now:             ''
    };

    @readonly
    static documentTypeLabels = {
        [DocumentType.Attachment]: 'DOCUMENT.TYPE.ATTACHMENT',
    };

    @readonly
    static terminalMediaType: Partial<Record<TerminalMediaType, string>> = {
        [TerminalMediaType.dynamic]:      'Tablet',
        [TerminalMediaType.static]:       'Druck',
        [TerminalMediaType.gt7]:          'Gantner GT7',
        [TerminalMediaType.gatConnector]: 'Gantner Connector',
        [TerminalMediaType.gatVending]:   'Gantner Vending',
    };

    // @readonly
    // static paymentTimeUnits = {
    //     [TimeUnit.Week]: 'Wochen',
    //     [TimeUnit.Day]:  'Tage'
    // };

    @readonly
    static surchargeValueType = {
        percent:  SurchargeValueType.percent,
        absolute: SurchargeValueType.absolute
    };

    @readonly
    static absenceCreditType = {
        doesNotExpire:      AbsenceCreditAccountType.doesNotExpire,
        expiresAfter:       AbsenceCreditAccountType.expiresAfter,
        expiresAtEndOfYear: AbsenceCreditAccountType.expiresAtEndOfYear

    };

    @readonly
    static absencePaymentType = {
        paid:                     AbsencePaymentType.paid,
        unpaid:                   AbsencePaymentType.unpaid,
        partly:                   AbsencePaymentType.partly,
        substractFromTimeAccount: AbsencePaymentType.substractFromTimeAccount,
        paidMaxDuration:          AbsencePaymentType.paidMaxDuration

    };

    @readonly
    static absenceVerificationType = {
        none:           AbsenceVerificationType.none,
        alwaysRequired: AbsenceVerificationType.alwaysRequired,
        requiredAfter:  AbsenceVerificationType.requiredAfter
    };

    @readonly
    static restTimeDurationUnits: TimeUnit[] = [TimeUnit.Day, TimeUnit.Week, TimeUnit.Month, TimeUnit.Year];

    @readonly
    static restTimeDurationUnitOptions: Option<TimeUnit>[] = Globals.restTimeDurationUnits.map(unit => ({
        label: Globals.timeUnit.default[unit],
        value: unit,
    }));

    @readonly
    static checkTimeRounding = {
        NoRounding:           CheckTimeRounding.NoRounding,
        RoundDown:            CheckTimeRounding.RoundDown,
        RoundUp:              CheckTimeRounding.RoundUp,
        RoundOnNMinutesAfter: CheckTimeRounding.RoundOnNMinutesAfter
    };

    @readonly
    static checkInTerminalOptions = {
        QRTerminal_checkin:   TerminalCheckinMode.QRTerminal_checkin,
        QRTerminal_checkout:  TerminalCheckinMode.QRTerminal_checkout,
        QRTerminal_pause:     TerminalCheckinMode.QRTerminal_pause,
        QRTerminal_pauseEnde: TerminalCheckinMode.QRTerminal_pauseEnde,
        IP_checkin:           TerminalCheckinMode.IP_checkin,
        IP_checkout:          TerminalCheckinMode.IP_checkout,
        IP_pause:             TerminalCheckinMode.IP_pause,
        IP_pauseEnd:          TerminalCheckinMode.IP_pauseEnd,
        ALL_checkin:          TerminalCheckinMode.IP_checkin | TerminalCheckinMode.QRTerminal_checkin,
        ALL_pause:            TerminalCheckinMode.IP_pause | TerminalCheckinMode.QRTerminal_pause,
        ALL_pauseEnde:        TerminalCheckinMode.IP_pauseEnd | TerminalCheckinMode.QRTerminal_pauseEnde,
        ALL_checkout:         TerminalCheckinMode.IP_checkout | TerminalCheckinMode.QRTerminal_checkout
    };

    @readonly
    static icons = {
        //"island-tropical": 'Palmeninsel tropische Insel',
        "viruses":           'Virus krank Erkrankung',
        "syringe":           'Spritze krank Erkrankung',
        "sun":               'Sonne',
        "car-side":          'Auto Reise',
        "plane":             'Flugzeug Reise verreisen Urlaub',
        "stethoscope":       'Stethoskop Arzt krank',
        "baby":              'Elternzeit Schwangerschaft Kind',
        "baby-carriage":     'Kinderwagen Elternzeit Kind',
        "building-columns":  'Universität Schule Bildung',
        "user-graduate":     'Universität Abschluss Weiterbildung',
        "graduation-cap":    'Universität Abschluss Weiterbildung',
        "person-chalkboard": 'Fortbildung Training Schulung',
        "book-open-reader":  'Ausbildung Berufsschule',
        "mountain-sun":      'Reisen Berge Urlaub',
        "passport":          'Reisepass Reisen Sabbatical',
        "door-closed":       'blockiert nicht verfügbar geschlossen',
        "paint-roller":      'Umzug renovieren',
        "ring":              'Ring Hochzeit',
        "cross":             'Kreuz Trauerfall Beerdigung',
        "place-of-worship":  'Gebetshaus Religion religiös',
        "wheelchair":        'Behinderung Barrierefreiheit',
        "snowflake":         'Schnee Eis Kälte Wetter',
        "umbrella":          'Wetter Regen Schirm',
        "umbrella-beach":    'Urlaub'
    };

    @readonly
    static iconsCheckInArea = {
        "house-user":         'Gebäude Filiale Homeoffice Home-Office',
        "house-chimney-user": 'Gebäude Filiale Homeoffice Home-Office',
        "house-flag":         'Gebäude Filiale Standort Lokation',
        "car":                'Dienstreise Auto',
        "handshake":          'Meeting Termin Besprechung Kundentermin',
        "warehouse":          'Gebäude Lager Produktion Standort',
        "person-chalkboard":  'Meeting Termin Besprechung Kundentermin',
        "plane":              'Dienstreise Flugzeug',
        "utensils":           'Essen Kundentermin Geschäftsessen ',

    };

    @readonly
    static scheduledTaskStatusLabels = {
        [ScheduledTaskStatus.pending]:   'Ausstehend',
        [ScheduledTaskStatus.running]:   'Läuft',
        [ScheduledTaskStatus.completed]: 'Abgeschlossen',
        [ScheduledTaskStatus.failure]:   'Fehler',
    };

    @readonly
    static VisibilityType = {
        [VisibilityType.all]:                      'Alle',
        [VisibilityType.custom]:                   'Bestimmte Abteilungen (inkl. Leitung und Stabstellen)',
        [VisibilityType.none]:                     'Nur sich selbst',
        [VisibilityType.department]:               'Diese Abteilung (inkl. Leitung und Stabsstellen)',
        [VisibilityType.department_subdepartment]: 'Diese Abteilung und untergeordnete Abteilungen (inkl. Leitung und Stabstellen)',
    };

    @readonly
    static VisibilityTargetLeader = {
        [VisibilityTargetLeader.leader]:            'Leitung',
        [VisibilityTargetLeader.leader_substitute]: 'Leitung und Vertretung',
    };

    @readonly
    static VisibilityName = VisibilityName;

    /**
     * **IMPORTANT**
     * Labels of new VisibilityName values must be added below to show within the orgchart.
     * Without adding a label users will not be able to configure the new visibility.
     */
    @readonly
    static VisibilityNameLabel: {[key in VisibilityName]?: string} = {
        absence_list:             "Abwesenheit (Übersicht und Auswertungen)",
        attendance_list:          "Check-in > Anwesenheit",
        orgchart:                 "Organisation > Organigramm",
        shift_planning:           "Schichtplanung",
        calendar:                 "Kalender",
        person_search:            "Personensuche",
        working_time_analyses:    "Auswertungen > Arbeitszeit",
        time_corrections_confirm: "Arbeitszeit Korrekturanträge",
        salary_stats:             "Auswertungen > Stundensatz",
    };

    @readonly
    static TerminalTrackingEvents = {
        none:                   TerminalTrackingEvents.none,
        checkin_checkout:       TerminalTrackingEvents.checkin_checkout,
        checkin_checkout_pause: TerminalTrackingEvents.checkin_checkout_pause
    };

    @readonly
    static WorkHoursType = {
        NumberOfHoursPerWeek: WorkHoursType.NumberOfHoursPerWeek,
        FixedSchedule:        WorkHoursType.FixedSchedule,
        ShiftPlanning:        WorkHoursType.ShiftPlanning
    };

    @readonly
    static OpeningTimeType = {
        Regular:    OpeningTimeType.Regular,
        Closed:     OpeningTimeType.Closed,
        Changed:    OpeningTimeType.Changed,
        LikeSunday: OpeningTimeType.LikeSunday,
    };

    @readonly
    static ShiftFailureReason: Record<ShiftFailureReason, string> = {
        "CONFLICT":            "Konflikt",
        "INVALID_DEPARTMENT":  "Ungültige Abteilung",
        "INVALID_USER":        "Ungültige Person",
        "MISSING_DEPARTMENT":  "Abteilung fehlt",
        "MISSING_START":       "Start fehlt",
        "MISSING_END":         "Ende fehlt",
        "MISSING_USER":        "Person fehlt",
        "IMPORT_OUT_OF_RANGE": "Übertrifft die maximale Importgröße",
        "UNSPECIFIED":         "Unspezifizierter Fehler",
    };

    @readonly
    static accountingAccountType: {[key in AccountingAccountType]?: string} = {
        [AccountingAccountType.financialAccount]:        'Bestandskonto',
        [AccountingAccountType.accountOfProceeds]:       'Erfolgskonto',
        [AccountingAccountType.performanceAccount]:      'Leistungskonto',
        [AccountingAccountType.vatAccount]:              'Mehrwertsteuerkonto',
        [AccountingAccountType.revenueReductionAccount]: 'Erlösminderungskonto ',
        [AccountingAccountType.badDebtAccount]:          'Forderungsausfallkonto ',
    };

    @readonly
    static accountingAccountTypeValues: {[key in AccountingAccountType]?: string} = {
        [AccountingAccountType.financialAccount]:        'financial_account',
        [AccountingAccountType.accountOfProceeds]:       'account_of_proceeds',
        [AccountingAccountType.performanceAccount]:      'performance_account',
        [AccountingAccountType.vatAccount]:              'vat_account',
        [AccountingAccountType.revenueReductionAccount]: 'revenue_reduction_account',
        [AccountingAccountType.badDebtAccount]:          'bad_debt_account',
    };

    @readonly
    static hmmCredentialType: {[key in HmmCredentialType]?: string} = {
        [HmmCredentialType.Proleos]:         'Proleos+',
        [HmmCredentialType.LeosHealthCloud]: 'LEOS.Health­Cloud',
    };

    @readonly
    static credRFIDType: {[key in RFIDType]?: string} = {
        [RFIDType.decimal]: 'Dezimal',
        [RFIDType.hex]:     'Hexadezimal',
    };

    @readonly
    static accountStatementsFormat: {[key in AccountStatementsFormat]?: string} = {
        [AccountStatementsFormat.none]:   'Kein Abruf',
        [AccountStatementsFormat.MT940]:  'MT940',
        [AccountStatementsFormat.Camt53]: 'CAMT.053',
    };

    @readonly
    static datevFlagWorkIncapacity: {[key in Datev_FlagWorkIncapacity]?: string} = {
        [Datev_FlagWorkIncapacity.HealthInsuranceNotResponsible]: 'Unzuständige Krankenkasse',
        [Datev_FlagWorkIncapacity.AU]:                            'AU',
        [Datev_FlagWorkIncapacity.Hospitalisation]:               'Krankhausaufenthalt',
        [Datev_FlagWorkIncapacity.ReportIsNotExisting]:           'eAU liegt nicht vor',
        [Datev_FlagWorkIncapacity.RehabilitationPrevention]:      'Reha / Vorsorge',
        [Datev_FlagWorkIncapacity.InpatientHospitalTreatment]:    'Teilstationäre Krankenhausbehandlung',
        [Datev_FlagWorkIncapacity.InVerification]:                'In Prüfung',
        [Datev_FlagWorkIncapacity.OtherEvidenceAvailable]:        'Anderer Nachweis liegt vor (ausländische oder privatärztliche AU)',
        [Datev_FlagWorkIncapacity.ForwardingProcedure]:           'Weiterleitungsverfahren',
    };

    @readonly
    static bookingMode = {
        DIRECT: BookingMode.DIRECT,
        GUIDED: BookingMode.GUIDED
    };

    @readonly
    static bookingAvailability = {
        Default:       BookingAvailability.Default,
        OnlyJoin:      BookingAvailability.OnlyJoin,
        JoinAndCreate: BookingAvailability.JoinAndCreate,
        NotAvailable:  BookingAvailability.NotAvailable,
    };

    @readonly
    static customerMode = {
        NewCustomer:      CustomerMode.NewCustomer,
        ExistingCustomer: CustomerMode.ExistingCustomer,
        ContactForm:      CustomerMode.ContactForm
    };

    @readonly
    static nameFormat = {
        FULL_NAME:      NameFormat.FULL_NAME,
        ONLY_FIRSTNAME: NameFormat.ONLY_FIRSTNAME,
        ONLY_LASTNAME:  NameFormat.ONLY_LASTNAME,
        PREF_FIRSTNAME: NameFormat.PREF_FIRSTNAME,
        PREF_LASTNAME:  NameFormat.PREF_LASTNAME
    };

    @readonly
    static tagType = {
        Undefined:             TagType.Undefined,
        Contract:              TagType.Contract,
        User:                  TagType.User,
        ContractFeeAdjustment: TagType.ContractFeeAdjustment,
        EmployeeContract:      TagType.EmployeeContract,
        Resource:              TagType.Resource,
        Event:                 TagType.Event,
        ProductCategory:       TagType.ProductCategory,
        Product:               TagType.Product
    };

    @readonly
    static eventOrigin = {
        administration: EventOrigin.administration,
        selfService:    EventOrigin.selfService,
        external:       EventOrigin.external,
        system:         EventOrigin.system
    };

    @readonly
    static EventInstanceState = {
        "placeholder": EventInstanceState.placeholder,
        "active":      EventInstanceState.active,
        "canceled":    EventInstanceState.canceled,
    };

    static getLightness() {
        const lightness = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ?
            Globals.DARK_MODE_LIGHTNESS :
            Globals.LIGHT_MODE_LIGHTNESS;
        return lightness;
    }

    static getHSL(HS: [number, number]){
        const [h, s] = HS ?? [undefined, undefined];
        if (!h || !s) return undefined;
        const lightness = Globals.getLightness();
        return `hsl(
            ${h}deg,
            ${s * 100}%,
            ${lightness * 100}%
        )`;
    }

    @readonly
    static bookingType = {
        unknown:               BookingType.unknown,
        contract:              BookingType.contract,
        directDebit:           BookingType.directDebit,
        cash:                  BookingType.cash,
        invoice:               BookingType.invoice,
        invoiceCheckout:       BookingType.invoiceCheckout,
        credit:                BookingType.credit,
        returnDebit:           BookingType.returnDebit,
        adjustingJournalEntry: BookingType.adjustingJournalEntry,
        claim:                 BookingType.claim,
        lossOfReceivables:     BookingType.lossOfReceivables,
    };

    @readonly
    static EUnsettledBooking = {
        earlierstPossible: EUnsettledBooking.earlierstPossible,
        latestPossible:    EUnsettledBooking.latestPossible,
    };

    @readonly
    static EbicsContactStatus = {
        Initializing:               EbicsContactStatus.Initializing,
        AutomaticInitializingEnded: EbicsContactStatus.AutomaticInitializingEnded,
        Active:                     EbicsContactStatus.Active,
        Inactive:                   EbicsContactStatus.InActive,
    };

	@readonly
	static tseConfigurationMode = {
		unknown: TseConfigurationMode.unknown,
		none:    TseConfigurationMode.none,
		short:   TseConfigurationMode.short,
		long:    TseConfigurationMode.long,
	};

    @readonly
    static FileType = {
        Default:              FileType.Default,
        Document:             FileType.Document,
        Excel:                FileType.Excel,
        Image:                FileType.Image,
        RestTimeVerification: FileType.RestTimeVerification,
    };

    @readonly
    static Mask = {
        None:   Mask.None,
        Circle: Mask.Circle,
        Square: Mask.Square,
    };
}
